import React from 'react';
import { MINUTE_OPTIONS, snapTo15 } from '../lib/format.js';

/**
 * Segmented minutes picker — 00 / 15 / 30 / 45.
 *
 * Replaces the v1.0 free numeric input. v2.0 constrains entry minutes to a
 * quarter-hour grid; the segmented control communicates the constraint
 * better than disabling out-of-step values inside a number input.
 *
 * Incoming `value` is snapped to the nearest 15-minute step for display so
 * legacy entries (created before v2.0 — e.g. 22m) render against a valid
 * segment when opened for edit. Persisting the snapped value back is the
 * parent's job — emit it from `onChange` on first interaction or on save.
 *
 * Props:
 *   value    — number (any integer in 0..59 accepted; non-quarters snap)
 *   onChange — (mins: number) => void
 *   size     — 'md' | 'sm' (sm matches the import-row controls)
 *   error    — boolean; mirrors the existing tt-segmented `is-error` styling
 *   disabled — boolean
 */
export function MinutesSegment({
  value,
  onChange,
  size = 'md',
  error = false,
  disabled = false,
}) {
  const snapped = snapTo15(value);
  const v = MINUTE_OPTIONS.includes(snapped) ? snapped : 0;
  const isSm = size === 'sm';
  const height = isSm ? 38 : 44;
  return (
    <div
      className={'tt-segmented ' + (error ? 'is-error' : '')}
      style={{
        '--cols': MINUTE_OPTIONS.length,
        height,
        ...(error ? { boxShadow: '0 0 0 1px #B30745 inset' } : {}),
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
      aria-disabled={disabled || undefined}
    >
      {MINUTE_OPTIONS.map((opt) => (
        <button
          key={opt}
          type="button"
          aria-pressed={v === opt}
          onClick={() => onChange(opt)}
          disabled={disabled}
          style={{
            height: '100%',
            fontFamily: 'inherit',
            fontVariantNumeric: 'tabular-nums',
            fontSize: isSm ? 13 : 14,
          }}
        >
          {String(opt).padStart(2, '0')}
        </button>
      ))}
    </div>
  );
}
